import React from "react";
import styled from "styled-components";
import PlusIcon from "./assets/plus.svg";
import BalancedImage from "./assets/createProject2.png";
import { Button, Group, Modal, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCreditCard, IconStar } from "@tabler/icons-react";
import useUserStore from "../stores/userStore";
import useLayoutStore from "../stores/useLayoutStore";
import { useTranslation } from "react-i18next";
import PostProjectModal from "./PostProjectModal";
import Mixpanel from "../components/Lib/mixpanel";


// ─────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────

const BaseStyles = styled.button`
  border-radius: 4px;
  display: flex;
  padding: ${({ $isToggle }) => ($isToggle ? "8px" : "10px 20px")};
  align-items: center;
  justify-content: center;
  gap: 10px;
  align-self: stretch;
  cursor: pointer;
  text-decoration: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  transition: all 0.2s ease;

  img, svg {
    margin-right: ${({ $isToggle }) => ($isToggle ? "0" : "8px")};
    width: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
    height: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
  }

  span {
    display: ${({ $isToggle }) => ($isToggle ? "none" : "inline")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(BaseStyles)`
  background: #3566fd;
  color: #fff;
  width: ${({ $isToggle }) => ($isToggle ? "40px" : "100%")};
  max-width: ${({ $isToggle }) => ($isToggle ? "40px" : "220px")};

  &:hover {
    background-color: #007bff;
    color: #ffffff;
  }
`;

const SecondaryButton = styled(BaseStyles)`
  border: 1px solid #d0d5e5;
  background: transparent;
  max-width: ${({ $isToggle }) => ($isToggle ? "40px" : "220px")};

  &:hover {
    background-color: #007bff;
    color: #ffffff;
  }
`;

const ValidateButton = styled(BaseStyles)`
  border: 1px solid #d0d5e5;
  background: #fff;
  backdrop-filter: blur(3px);
  width: ${({ $isToggle }) => ($isToggle ? "40px" : "100%")};
  height: ${({ $isToggle }) => ($isToggle ? "40px" : "auto")};
  padding: ${({ $isToggle }) => ($isToggle ? "8px" : "10px 20px")};

  &:hover {
    background-color: #0056b3;
    color: #ffffff;
  }

  img {
    width: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
    height: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
    margin-right: ${({ $isToggle }) => ($isToggle ? "0" : "8px")};
  }
`;

const BillingButton = styled(BaseStyles)`
  background: linear-gradient(135deg, #3566fd 0%, #007bff 50%, #5b9aff 100%);
  color: #ffffff;
  border: none;
  width: ${({ $isToggle }) => ($isToggle ? "40px" : "100%")};
  max-width: ${({ $isToggle }) => ($isToggle ? "40px" : "220px")};
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(53, 102, 253, 0.4);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #007bff 0%, #3566fd 50%, #5b9aff 100%);
    box-shadow: 0 6px 20px rgba(53, 102, 253, 0.6);
    transform: translateY(-1px);
  }

  img, svg {
    margin-right: ${({ $isToggle }) => ($isToggle ? "0" : "8px")};
    width: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
    height: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
    filter: brightness(0) invert(1);
  }
`;
const UpgradeButton = styled(BaseStyles)`
  background: linear-gradient(135deg, #3566fd 0%, #007bff 50%, #5b9aff 100%);
  color: #ffffff;
  border: none;
  width: ${({ $isToggle }) => ($isToggle ? "40px" : "100%")};
  max-width: ${({ $isToggle }) => ($isToggle ? "40px" : "220px")};
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(53, 102, 253, 0.4);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #007bff 0%, #3566fd 50%, #5b9aff 100%);
    box-shadow: 0 6px 20px rgba(53, 102, 253, 0.6);
    transform: translateY(-1px);
  }

  img, svg {
    margin-right: ${({ $isToggle }) => ($isToggle ? "0" : "8px")};
    width: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
    height: ${({ $isToggle }) => ($isToggle ? "20px" : "auto")};
    filter: brightness(0) invert(1);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $isToggle }) => ($isToggle ? "5px" : "10px")};
  border-radius: 10px;
  background: #fff;
  padding: ${({ $isToggle }) => ($isToggle ? "5px" : "10px")};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  gap: 10px;
  width: 100%;
`;

const Content = styled.div`
  border-radius: 10px;
  background: #f6f8ff;
  padding: ${({ $isToggle }) => ($isToggle ? "0px" : "20px")};
  text-align: left;
  display: flex;
  flex-direction: ${({ $isToggle }) => ($isToggle ? "row" : "column")};
  align-items: ${({ $isToggle }) => ($isToggle ? "center" : "flex-start")};
  justify-content: ${({ $isToggle }) => ($isToggle ? "center" : "flex-start")};
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 8px;
  display: ${({ $isToggle }) => ($isToggle ? "none" : "block")};
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: #666666;
  margin-bottom: 16px;
  max-width: 80ch;
  display: ${({ $isToggle }) => ($isToggle ? "none" : "block")};
`;

const ProjectLimitWarning = styled.p`
  font-size: 14px;
  color: red;
  max-width: 80ch;
  border: 1px solid red;
  background: #ffebeb;
  padding: 8px;
`;

const NewUserContainer = styled.div`
  display: flex;
  padding: 20px 21px;
  align-items: center;
  gap: 40px;
  align-self: stretch;
  border-radius: 8px;
  background: #fff;
  @media (max-width: 834px) {
    flex-direction: column;
    gap: 16px;
    padding: 16px 14px;
  }
`;

const ImageContainer = styled.img`
  width: 400px;
  height: 200px;
  border-radius: 10px;
  background: #f6f8ff;
  object-fit: cover;
  @media (max-width: 834px) {
  display: none;
  }
`;

const NewUserContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 834px) {
    align-items: center;
    text-align: center;
    gap: 12px;
  }
`;

// ─────────────────────────────────────────────
// Tooltip Custom Styles
// ─────────────────────────────────────────────

const tooltipStyles = {
  tooltip: {
    backgroundColor: "#F6F8FF",
    color: "var(--color-button-accent)",
    border: "1px solid var(--color-button-accent)",
  },
};

// ─────────────────────────────────────────────
// StyledButton Component with Tooltip Support
// ─────────────────────────────────────────────

export const StyledButton = ({
  variant = "primary",
  tooltipLabel, // new prop for tooltip text
  href,
  onClick,
  children,
  disabled = false,
  ...props
}) => {
  const { isToggle } = useLayoutStore();

  const ButtonComponent = {
    primary: PrimaryButton,
    secondary: SecondaryButton,
    validate: ValidateButton,
    billing: BillingButton,
    upgrade: UpgradeButton
  }[variant];

  // Convert children to an array to extract text and icon
  const childrenArray = React.Children.toArray(children);
  const buttonIcon = childrenArray.find(
    (child) => React.isValidElement(child) && (child.type === "img" || child.type?.displayName === "IconCreditCard" || child.type?.displayName === "IconStar" || child.type?.name?.includes("Icon"))
  );
  const buttonText = childrenArray.find((child) => typeof child === "string");

  const renderIcon = () => {
    if (!buttonIcon) return null;
    // When collapsed and tooltipLabel is provided, wrap the icon with a tooltip.
    if (isToggle && tooltipLabel) {
      return (
        <Tooltip
          label={tooltipLabel}
          position="right"
          withArrow
          styles={tooltipStyles}
          offset={11}
                      transitionProps={{ transition: 'pop-bottomo-left', duration: 300 }}
        >
          {buttonIcon}
        </Tooltip>
      );
    }
    return buttonIcon;
  };

  const renderContent = () => (
    <>
      {!isToggle && buttonText && <span>{buttonText}</span>}
      {renderIcon()}
    </>
  );

  const buttonNode = href ? (
    <ButtonComponent
      as="a"
      href={disabled ? undefined : href}
      aria-disabled={disabled}
      $isToggle={isToggle}
      {...props}
    >
      {renderContent()}
    </ButtonComponent>
  ) : (
    <ButtonComponent
      onClick={disabled ? undefined : onClick}
      type="button"
      $isToggle={isToggle}
      disabled={disabled}
      {...props}
    >
      {renderContent()}
    </ButtonComponent>
  );

  if (tooltipLabel) {
    // Only apply full width wrapper to billing button (sidebar only) to match button width
    // Primary button will stretch naturally via Header's align-items: stretch
    const wrapperStyle = !isToggle && variant === "billing" ? { display: 'block', width: '100%' } : {};
    return (
      <Tooltip label={tooltipLabel} position="right" withArrow styles={tooltipStyles} withinPortal>
        <span style={wrapperStyle}>{buttonNode}</span>
      </Tooltip>
    );
  }

  return buttonNode;
};

// ─────────────────────────────────────────────
// SidebarAction Component
// ─────────────────────────────────────────────

const SidebarAction = ({ sidebar = false }) => {
  const [opened, { close, open }] = useDisclosure(false);
  const {
    user: { user },
    can_do_prd,
    plan_tier
  } = useUserStore((state) => ({ user: state.user, can_do_prd: state.can_do_prd, plan_tier: state.plan_tier }));

  const isPaidPlan =  plan_tier && plan_tier !== "free";
  const { t } = useTranslation();
  const { isToggle } = useLayoutStore();

  const projectLimitState = user?.limit_states?.projects || {};
  const projectLimitReached = projectLimitState.limit_reached;
  const projectLimitMessage = projectLimitReached
    ? `You've reached the ${projectLimitState.limit}-project cap on your plan. Archive a project or upgrade to add more.`
    : "";
  const createProjectTooltip = projectLimitReached
    ? projectLimitMessage
    : t("sidebar_action.create_project");
  const createNewProjectTooltip = projectLimitReached
    ? projectLimitMessage
    : t("sidebar_action.create_new_project");

  const handleCreateProjectAction = (eventName, locationLabel) => {
    if (!user) return;

    if (projectLimitReached) {
      Mixpanel.button(eventName, {
        location: locationLabel,
        action_outcome: "Limit Reached",
        user_profile_complete: user.profile_complete,
        user_id: user.id,
      });
      return;
    }

    if (!user.profile_complete) {
      open();
      Mixpanel.button(eventName, {
        location: locationLabel,
        action_outcome: "Opened Profile Completion Modal",
        user_profile_complete: user.profile_complete,
        user_id: user.id,
      });
      return;
    }

    Mixpanel.button(eventName, {
      location: locationLabel,
      action_outcome: "Navigated to New Project Page",
      user_profile_complete: user.profile_complete,
      user_id: user.id,
    });
    window.location.href = "/projects/new";
  };

  return (
    <>
      <Modal opened={opened} onClose={close} size="lg" withCloseButton={false}>
        <div className="p-4 overflow-y-auto">
          <p className="mt-1 text-gray-8000">
            {t("modal.profile_access_message")}
          </p>
        </div>
        <div className="flex justify-end items-center gap-x-2 py-3 px-4">
          <Button onClick={close} variant="default" className="px-12">
            {t("modal.cancel_button")}
          </Button>
          <Button
            href="/onboarding"
            component="a"
            variant="light"
            color="indigo"
            className="text-blue-700 px-12"
          >
            {t("modal.complete_profile_button")}
          </Button>
        </div>
      </Modal>

      {!sidebar ? (
        <NewUserContainer>
          <ImageContainer src={BalancedImage} alt="Perfectly Balanced Image" />
          <NewUserContent>
            <div>
              <Title>{t("sidebar_action.kickstart_collaboration")}</Title>
              <Subtitle>
                {t("sidebar_action.kickstart_collaboration_subheading")}
              </Subtitle>
            </div>
            {projectLimitReached && (
              <ProjectLimitWarning>{projectLimitMessage}</ProjectLimitWarning>
            )}
            <Group>
              {!projectLimitReached && (
                <>
                  <StyledButton
                    tooltipLabel={createProjectTooltip}
                    variant="primary"
                    disabled={projectLimitReached}
                    onClick={() =>
                      handleCreateProjectAction(
                        "Create Project Button",
                        "Dashboard page"
                      )
                    }
                  >
                    {t("sidebar_action.create_project")}
                    <img
                      src={PlusIcon}
                      alt={t("sidebar_action.create_project")}
                    />
                  </StyledButton>

                  <PostProjectModal
                    disabled={projectLimitReached}
                    limitMessage={projectLimitMessage}
                  />
                </>
              )}

              {/* Scoper Button */}
              <StyledButton
                tooltipLabel="Scoper --"
                variant="secondary"
                onClick={async () => {
                  try {
                    // Generate OAuth code and redirect to PMS workspace
                    const response = await fetch(
                      "/api/pms/workspace_redirect",
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          "X-CSRF-Token": document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute("content"),
                        },
                      }
                    );

                    if (response.ok) {
                      const { redirect_url } = await response.json();
                      // Open PMS workspace in new tab
                      window.open(redirect_url, "_blank");
                    } else {
                      console.error("Failed to generate PMS redirect");
                    }
                  } catch (error) {
                    console.error("Error redirecting to PMS:", error);
                  }
                }}
              >
                Scoper
                <img src={PlusIcon} alt="Scoper" />
              </StyledButton>

              {/* Upgrade to Pro Button */}
              {projectLimitReached && <GoProButton />}
            </Group>
          </NewUserContent>
        </NewUserContainer>
      ) : (
        <Container $isToggle={isToggle}>
          <Header>
            <StyledButton
              tooltipLabel={createNewProjectTooltip}
              variant="primary"
              disabled={projectLimitReached}
              onClick={() =>
                handleCreateProjectAction(
                  "Create New Project Button",
                  "Sidebar"
                )
              }
            >
              {t("sidebar_action.create_new_project")}
              <img
                src={PlusIcon}
                alt={t("sidebar_action.create_new_project")}
              />
            </StyledButton>
           { !isPaidPlan &&  <GoProButton />}
          </Header>
        </Container>
      )}
    </>
  );
};

const GoProButton = () => {
  return (
    <StyledButton
      tooltipLabel="Upgrade to Pro"
      variant="upgrade"
      onClick={async () => {
        try {
          // Generate OAuth code and redirect to Scoper billing
          const response = await fetch("/api/pms/billing_redirect", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content"),
            },
          });

          if (response.ok) {
            const { redirect_url } = await response.json();
            // Redirect to Scoper billing page
            window.location.href = redirect_url;
          } else {
            console.error("Failed to generate billing redirect");
            alert("Failed to redirect to billing. Please try again.");
          }
        } catch (error) {
          console.error("Error redirecting to billing:", error);
          alert("An error occurred. Please try again.");
        }
      }}
    >
      Go Pro
      <IconStar size={18} />
    </StyledButton>
  );
};

export default SidebarAction;
